import {
  applySnapshot,
  flow,
  getEnv,
  getParentOfType,
  getRoot,
  types,
} from 'mobx-state-tree';

import RequestState, { RequestStateType } from '../../types/RequestState';
import FullProduct from './FullProduct';
import FullProductCollectionItem from './FullProductCollectionItem';
import ProductMatrix from './ProductMatrix';
import ProductProperty from './ProductProperty';

const FullProductMatrix = types.compose(
  ProductMatrix,
  types
    .model('FullProductMatrix', {
      row: ProductProperty,
      column: ProductProperty,
      items: types.array(FullProductCollectionItem),
      stocksInfoState: types.optional(RequestStateType, RequestState.NONE),
    })
    .actions((self) => {
      return {
        afterCreate() {
          const { accountStore, configStore } = getRoot(self);

          if (
            accountStore?.showCollectionProductMatrix &&
            configStore?.productPage?.matrixDetailedStockInfoEnabled
          ) {
            self.loadStocksInfo();
          }
        },
        loadStocksInfo: flow(function* loadStocksInfo({
          rowId = null,
          columnId = null,
        } = {}) {
          if (self.stocksInfoState === RequestState.LOADING) {
            return;
          }

          self.stocksInfoState = RequestState.LOADING;
          try {
            const params = {};

            if (rowId !== null) {
              params.rowId = rowId;
            }
            if (columnId !== null) {
              params.columnId = columnId;
            }

            const stocksInfoData = yield getEnv(self).apiWrapper.request(
              `stocks-info/${self.product.id}`,
              { params },
              { active_section: null }
            );

            self.patchStocksInfoToItems(stocksInfoData);
            self.stocksInfoState = RequestState.LOADED;
            return stocksInfoData;
          } catch (e) {
            self.stocksInfoState = RequestState.ERROR;
            throw e;
          }
        }),
        patchStocksInfoToItems(stocksInfoData) {
          Object.keys(stocksInfoData).forEach((rowKey) => {
            const rowId = parseInt(rowKey, 10);
            const rowData = stocksInfoData[rowKey];

            Object.keys(rowData).forEach((columnKey) => {
              const columnId = parseInt(columnKey, 10);
              const cellData = rowData[columnKey];

              const item = self.items.find((item) => {
                return item.row_id === rowId && item.column_id === columnId;
              });

              if (item && item.product) {
                const productSnapshot = {
                  ...item.product,
                  free_quantity: cellData.free_quantity,
                  in_stock_quantity: cellData.in_stock_quantity,
                  incoming_quantities: cellData.incoming_quantities || [],
                };

                applySnapshot(item.product, productSnapshot);

                if (cellData.storages) {
                  const extendedId = item.product.id;
                  item.stocks.set(extendedId, cellData);
                  item.stockStates.set(extendedId, RequestState.LOADED);
                }
              }
            });
          });
        },
      };
    })
    .views((self) => {
      return {
        get product() {
          return getParentOfType(self, FullProduct);
        },
        getStoragesForRow(rowId) {
          const storageMap = new Map();
          const isSwapped = self.shouldSwapCollectionOrder();

          self.items.forEach((item) => {
            const itemId = isSwapped ? item.row_id : item.column_id;
            if (itemId !== rowId) {
              return;
            }

            const stockData = item.stocks.get(item.product.id);
            if (stockData && stockData.storageSummaries) {
              stockData.storageSummaries.forEach((storage) => {
                if (!storageMap.has(storage.id)) {
                  storageMap.set(storage.id, storage);
                }
              });
            }
          });

          return Array.from(storageMap.values());
        },
      };
    })
);

export default FullProductMatrix;
